/**
 * PandaPRO — Operational Metrics
 *
 * Rastreia métricas de uso e saúde operacional.
 * - Sem dados financeiros privados dos usuários
 * - Agrega eventos por tipo/fluxo
 * - Buffer circular em memória (últimas 200 entradas)
 * - Persiste eventos críticos em audit_logs via Supabase
 */

import { supabase } from './supabase';

export type MetricEvent =
  | 'login'
  | 'create_transaction'
  | 'update_transaction'
  | 'delete_transaction'
  | 'create_transfer'
  | 'delete_transfer'
  | 'pay_card_invoice'
  | 'create_account'
  | 'create_card'
  | 'create_goal'
  | 'add_goal_contribution'
  | 'plan_limit_hit'
  | 'plan_limit_check_fail'
  | 'edge_function_error'
  | 'validation_error'
  | 'export_csv'
  | 'export_pdf'
  | 'ai_question'
  | 'feature_access_denied';

interface MetricEntry {
  event: MetricEvent;
  ts: number;       // Date.now()
  flow?: string;
  success?: boolean;
  durationMs?: number;
}

// ── In-memory circular buffer (últimas 200 entradas) ─────────────────────────
const BUFFER_SIZE = 200;
const buffer: MetricEntry[] = [];

const push = (entry: MetricEntry): void => {
  if (buffer.length >= BUFFER_SIZE) buffer.shift();
  buffer.push(entry);
};

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Registra um evento de métrica.
 * Silencioso — nunca quebra o fluxo principal.
 */
export const trackMetric = (
  event: MetricEvent,
  opts?: { flow?: string; success?: boolean; durationMs?: number }
): void => {
  try {
    push({ event, ts: Date.now(), ...opts });
  } catch {
    // never throw
  }
};

/**
 * Retorna agregação dos eventos em memória dos últimos N minutos.
 * Útil para diagnóstico no admin panel sem expor dados individuais.
 */
export const getMetricsSummary = (windowMinutes = 60): {
  total: number;
  byEvent: Record<string, number>;
  errorRate: number;
  recentErrors: { event: MetricEvent; flow?: string; ts: number }[];
} => {
  const cutoff = Date.now() - windowMinutes * 60_000;
  const recent = buffer.filter(e => e.ts >= cutoff);

  const byEvent: Record<string, number> = {};
  let errors = 0;
  const recentErrors: { event: MetricEvent; flow?: string; ts: number }[] = [];

  for (const entry of recent) {
    byEvent[entry.event] = (byEvent[entry.event] ?? 0) + 1;
    if (entry.success === false) {
      errors++;
      if (recentErrors.length < 10) {
        recentErrors.push({ event: entry.event, flow: entry.flow, ts: entry.ts });
      }
    }
  }

  return {
    total: recent.length,
    byEvent,
    errorRate: recent.length > 0 ? errors / recent.length : 0,
    recentErrors,
  };
};

/**
 * Persiste um evento crítico em audit_logs.
 * Chamado apenas para eventos de negócio relevantes.
 * Falha silenciosa — nunca quebra o fluxo principal.
 */
export const persistMetric = async (
  event: MetricEvent,
  userId: string,
  context?: Record<string, unknown>
): Promise<void> => {
  try {
    await supabase.from('audit_logs').insert({
      user_id: userId,
      actor_id: userId,
      action: 'create',
      resource: 'metrics',
      resource_id: null,
      target_type: 'metric_event',
      target_id: null,
      details: JSON.stringify({ event, ...context }),
    });
  } catch {
    // never throw
  }
};

/**
 * Retorna os últimos N eventos brutos do buffer.
 * Uso interno / diagnóstico.
 */
export const getRecentEvents = (limit = 50): MetricEntry[] =>
  [...buffer].reverse().slice(0, limit);
